import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { EmailTemplate } from '../../types/entity.types';
import { SagaPayload } from '../api.types';
import {
  SaveEmailTemplatesRequest,
  SaveEmailTemplatesResponse,
  TestEmailTemplateRequest,
  ViewEmailTemplatesRequest,
  ViewEmailTemplatesResponse
} from './email-template.types';

import {
  RETRIEVING_EMAIL_TEMPLATES,
  SAVING_EMAIL_TEMPLATES,
  SENDING_TEST_EMAIL_TEMPLATE
} from './email-template.constants';

import {
  requestSaveEmailTemplates,
  requestTestEmailTemplate,
  requestViewEmailTemplates
} from './email-template.actions';

import {
  getSaveEmailTemplatesRequest,
  getTestEmailTemplateRequest,
  getViewEmailTemplatesRequest
} from './email-template.endpoints';

import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performSaveEmailTemplatesRequest({
  payload
}: SagaPayload<SaveEmailTemplatesRequest, EmailTemplate[]>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(SAVING_EMAIL_TEMPLATES));

    const [endpoint, options] = getSaveEmailTemplatesRequest(requestData);

    const { data }: AxiosResponse<SaveEmailTemplatesResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.emailTemplates);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(SAVING_EMAIL_TEMPLATES));
  }
}

export function* watchForSaveEmailTemplatesRequest() {
  yield takeLatest(requestSaveEmailTemplates.type, performSaveEmailTemplatesRequest);
}

export function* performViewEmailTemplatesRequest({
  payload
}: SagaPayload<ViewEmailTemplatesRequest, EmailTemplate[]>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_EMAIL_TEMPLATES));

    const [endpoint, options] = getViewEmailTemplatesRequest(requestData);

    const { data }: AxiosResponse<ViewEmailTemplatesResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.emailTemplates);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_EMAIL_TEMPLATES));
  }
}

export function* watchForViewEmailTemplatesRequest() {
  yield takeLatest(requestViewEmailTemplates.type, performViewEmailTemplatesRequest);
}

export function* performTestEmailTemplateRequest({ payload }: SagaPayload<TestEmailTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(SENDING_TEST_EMAIL_TEMPLATE));

    const [endpoint, options] = getTestEmailTemplateRequest(requestData);

    yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: 'Test email sent successfully' }));

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(SENDING_TEST_EMAIL_TEMPLATE));
  }
}

export function* watchForTestEmailTemplateRequest() {
  yield takeLatest(requestTestEmailTemplate.type, performTestEmailTemplateRequest);
}

export default function* emailTemplateSaga() {
  yield all([
    watchForSaveEmailTemplatesRequest(),
    watchForViewEmailTemplatesRequest(),
    watchForTestEmailTemplateRequest()
  ]);
}
